import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { LibraryModule } from '../shared/lib/lib.module';
import { NiTableComponent } from '../shared/lib/ni-table/ni-table.component';
import { NiTableCellDirective } from '../shared/lib/ni-table/ni-table-cell.directive';
import { NiTableColumn } from '../shared/lib/ni-table/ni-table.models';
import {
  ActivityAdminService,
  ActivityBulkRowError,
  ActivityCreateRequest,
  ActivityStatus,
  AdminActivityItem,
} from '../core/services/activity-admin.service';
import { LocationService } from '../core/services/location.service';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import {
  ACTIVITY_DOMAIN_OPTIONS,
  ACTIVITY_TYPE_OPTIONS,
  domainsForActivityType,
} from '../shared/utils/activity-type-domain.map';
import {
  ACTIVITY_MONTHLY_OCCURRENCE_OPTIONS,
  isMonthlyOccurrenceValue,
  normalizeMonthlyOccurrence,
  normalizeWeeklyDay,
} from '../shared/utils/activity-recurrence';

export type ActivityListRow = {
  id: string;
  activity: string;
  code: string;
  type: string;
  special: boolean;
  reoccurring: string;
  cap: string;
  description: string;
  domainsOfWellness: string;
  status: string;
  statusLabel: string;
  fromDate: string;
  thruDate: string;
};

@Component({
  selector: 'activity-management-component',
  standalone: true,
  templateUrl: './activity-management.component.html',
  styleUrls: ['./activity-management.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    LibraryModule,
    NiTableComponent,
    NiTableCellDirective,
  ],
})
export class ActivityManagementComponent implements OnInit {
  @ViewChild('bulkFileInput') bulkFileInput?: ElementRef<HTMLInputElement>;

  isInitialLoading = true;
  isFilterOpen = false;
  openActionMenuId: string | null = null;
  searchText = '';
  errorMessage: string | null = null;
  isDuplicating = false;
  duplicatingId: string | null = null;

  isBulkUploadOpen = false;
  isBulkUploading = false;
  bulkSelectedFile: File | null = null;
  bulkSelectedFileName = '';
  bulkErrorMessage: string | null = null;
  bulkSuccessMessage: string | null = null;
  bulkRowErrors: ActivityBulkRowError[] = [];

  activityList: ActivityListRow[] = [];
  filteredList: ActivityListRow[] = [];
  columns: NiTableColumn<ActivityListRow>[] = [];

  filterActivity = '';
  filterTypes: string[] = [];
  filterSpecialEvent: '' | 'yes' | 'no' = '';
  filterReoccurring: string[] = [];
  filterDomain = '';
  filterFromDate = '';
  filterToDate = '';
  activityOptions: string[] = [];

  /** Must match Activity Screen tabs: Game / Health & Wellness / Educational */
  readonly typeOptions = [...ACTIVITY_TYPE_OPTIONS];
  /** Client filter: One Time vs Recurring (Weekly + Monthly). */
  readonly reoccurringOptions = ['One Time', 'Recurring'];
  /** Must match activity create/edit form domain options */
  readonly domainOptions = [...ACTIVITY_DOMAIN_OPTIONS];
  readonly yesNoOptions = ['Yes', 'No'];
  readonly timeModeOptions = ['all_day', 'specific'];
  /** Centers are closed Sat/Sun — weekly series is Mon–Fri only. */
  readonly reoccurringDayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  /** Monthly series: 1st–4th × Monday–Friday. */
  readonly monthlyOccurrenceOptions = [...ACTIVITY_MONTHLY_OCCURRENCE_OPTIONS];
  readonly statusOptions = ['active', 'draft', 'inactive'];
  centerOptions: string[] = [];

  private readonly bulkTemplateHeaders = [
    'activityName',
    'code',
    'type',
    'description',
    'limitedCapacity',
    'center',
    'isSpecial',
    'isSeries',
    'serviceDeliveryNeeded',
    'timeMode',
    'startTime',
    'endTime',
    'reoccurring',
    'reoccurringDays',
    'fromDate',
    'thruDate',
    'status',
  ] as const;

  constructor(
    private readonly router: Router,
    private readonly activityAdminService: ActivityAdminService,
    private readonly locationService: LocationService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    this.columns = [
      { key: 'activity', label: 'Activity' },
      { key: 'code', label: 'Code' },
      { key: 'type', label: 'Type' },
      { key: 'special', label: 'Special Activity', custom: true },
      { key: 'reoccurring', label: 'Reoccurring' },
      { key: 'cap', label: 'Cap' },
      { key: 'description', label: 'Description' },
      { key: 'domainsOfWellness', label: 'Domains of Wellness' },
      { key: 'statusLabel', label: 'Status' },
      { key: 'action', label: 'Action', custom: true, align: 'center' },
    ];

    this.loadCenterOptions();
    this.loadActivities();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openActionMenuId = null;
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center']);
  }

  onSearchInput(value: string): void {
    this.searchText = value ?? '';
    this.applyFilters();
  }

  toggleFilterPanel(): void {
    this.isFilterOpen = !this.isFilterOpen;
  }

  closeFilterPanel(): void {
    this.isFilterOpen = false;
  }

  toggleType(type: string): void {
    if (this.filterTypes.includes(type)) {
      this.filterTypes = this.filterTypes.filter((item) => item !== type);
    } else {
      this.filterTypes = [...this.filterTypes, type];
    }
  }

  removeType(type: string): void {
    this.filterTypes = this.filterTypes.filter((item) => item !== type);
  }

  toggleReoccurring(value: string): void {
    if (this.filterReoccurring.includes(value)) {
      this.filterReoccurring = this.filterReoccurring.filter((item) => item !== value);
    } else {
      this.filterReoccurring = [...this.filterReoccurring, value];
    }
  }

  resetFilters(): void {
    this.filterActivity = '';
    this.filterTypes = [];
    this.filterSpecialEvent = '';
    this.filterReoccurring = [];
    this.filterDomain = '';
    this.filterFromDate = '';
    this.filterToDate = '';
    this.applyFilters();
  }

  applyFilters(): void {
    const query = this.searchText.trim().toLowerCase();
    const selectedActivity = this.filterActivity.trim().toLowerCase();
    const fromDate = this.filterFromDate.trim();
    const toDate = this.filterToDate.trim();

    this.filteredList = this.activityList.filter((row) => {
      const matchesSearch =
        !query ||
        row.activity.toLowerCase().includes(query) ||
        row.code.toLowerCase().includes(query) ||
        row.type.toLowerCase().includes(query) ||
        row.description.toLowerCase().includes(query);

      const matchesActivity =
        !selectedActivity || row.activity.trim().toLowerCase() === selectedActivity;

      const matchesType =
        this.filterTypes.length === 0 || this.filterTypes.includes(row.type);

      const matchesSpecial =
        !this.filterSpecialEvent ||
        (this.filterSpecialEvent === 'yes' ? row.special : !row.special);

      const matchesReoccurring =
        this.filterReoccurring.length === 0 ||
        this.filterReoccurring.some((option) => {
          const value = (row.reoccurring ?? '').toLowerCase();
          if (option === 'One Time') {
            return value.includes('one time');
          }
          if (option === 'Recurring') {
            return value.includes('weekly') || value.includes('monthly');
          }
          return value.includes(option.toLowerCase());
        });

      const matchesDomain =
        !this.filterDomain ||
        row.domainsOfWellness.toLowerCase().includes(this.filterDomain.toLowerCase());

      const matchesDateRange = this.matchesDateRange(row, fromDate, toDate);

      return (
        matchesSearch &&
        matchesActivity &&
        matchesType &&
        matchesSpecial &&
        matchesReoccurring &&
        matchesDomain &&
        matchesDateRange
      );
    });
    this.isFilterOpen = false;
  }

  /** Activity overlaps [fromDate, toDate] when either bound is set (same idea as activity report). */
  private matchesDateRange(row: ActivityListRow, fromDate: string, toDate: string): boolean {
    if (!fromDate && !toDate) {
      return true;
    }

    const activityStart = row.fromDate;
    const activityEnd = row.thruDate || row.fromDate;
    if (!activityStart) {
      return false;
    }

    if (fromDate && activityEnd < fromDate) {
      return false;
    }
    if (toDate && activityStart > toDate) {
      return false;
    }
    return true;
  }

  private refreshActivityOptions(): void {
    const names = new Set<string>();
    for (const row of this.activityList) {
      const name = row.activity?.trim();
      if (name && name !== '—') {
        names.add(name);
      }
    }
    this.activityOptions = Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );

    if (this.filterActivity && !names.has(this.filterActivity)) {
      this.filterActivity = '';
    }
  }

  /** Export the currently filtered activity list as an Excel spreadsheet. */
  onDownload(): void {
    if (!this.filteredList.length) {
      return;
    }

    const sheetRows = this.filteredList.map((row) => ({
      Activity: row.activity === '—' ? '' : row.activity,
      Code: row.code === '—' ? '' : row.code,
      Type: row.type === '—' ? '' : row.type,
      'Special Activity': row.special ? 'Yes' : 'No',
      Reoccurring: row.reoccurring === '—' ? '' : row.reoccurring,
      Cap: row.cap,
      Description: row.description === '—' ? '' : row.description,
      'Domains of Wellness': row.domainsOfWellness === '—' ? '' : row.domainsOfWellness,
      Status: row.statusLabel,
      'From Date': row.fromDate || '',
      'Thru Date': row.thruDate || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Activities');
    XLSX.writeFile(workbook, `activities-${this.downloadSuffix()}.xlsx`);
  }

  private downloadSuffix(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  }

  openBulkUploadModal(): void {
    this.isBulkUploadOpen = true;
    this.resetBulkUploadState();
  }

  closeBulkUploadModal(): void {
    if (this.isBulkUploading) {
      return;
    }
    this.isBulkUploadOpen = false;
    this.resetBulkUploadState();
  }

  async downloadBulkTemplate(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'BakerRipley Staff Portal';

    const sheet = workbook.addWorksheet('Activities', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    const lists = workbook.addWorksheet('Lists');

    sheet.columns = this.bulkTemplateHeaders.map((header) => ({
      header,
      key: header,
      width: Math.max(14, header.length + 2),
    }));
    sheet.getRow(1).font = { bold: true };

    // limitedCapacity is free-entry (any whole number) — no dropdown validation.
    const listColumns: Array<{ key: string; values: string[] }> = [
      { key: 'type', values: [...this.typeOptions] },
      { key: 'center', values: this.centerOptions.length ? [...this.centerOptions] : [] },
      { key: 'isSpecial', values: [...this.yesNoOptions] },
      { key: 'isSeries', values: [...this.yesNoOptions] },
      { key: 'serviceDeliveryNeeded', values: [...this.yesNoOptions] },
      { key: 'timeMode', values: [...this.timeModeOptions] },
      { key: 'reoccurring', values: [...this.reoccurringOptions] },
      {
        key: 'reoccurringDays',
        values: [...this.reoccurringDayOptions, ...this.monthlyOccurrenceOptions],
      },
      { key: 'status', values: [...this.statusOptions] },
    ];

    listColumns.forEach((column, index) => {
      const excelCol = index + 1;
      lists.getCell(1, excelCol).value = column.key;
      lists.getCell(1, excelCol).font = { bold: true };
      column.values.forEach((value, rowIndex) => {
        lists.getCell(rowIndex + 2, excelCol).value = value;
      });
    });
    lists.state = 'hidden';

    const dataRowCount = 200;
    type BulkHeader = (typeof this.bulkTemplateHeaders)[number];
    const headerIndex = new Map<BulkHeader, number>(
      this.bulkTemplateHeaders.map((header, index) => [header, index + 1]),
    );

    const addListValidation = (
      header: BulkHeader,
      listColIndex: number,
      listLength: number,
    ): void => {
      const col = headerIndex.get(header);
      if (!col || listLength < 1) {
        return;
      }
      const colLetter = this.excelColumnLetter(col);
      const listLetter = this.excelColumnLetter(listColIndex);
      // exceljs runtime supports dataValidations; typings can omit it depending on import shape
      const sheetWithValidation = sheet as ExcelJS.Worksheet & {
        dataValidations: {
          add: (
            range: string,
            validation: {
              type: 'list';
              allowBlank?: boolean;
              showErrorMessage?: boolean;
              errorTitle?: string;
              error?: string;
              formulae: string[];
            },
          ) => void;
        };
      };
      sheetWithValidation.dataValidations.add(`${colLetter}2:${colLetter}${dataRowCount + 1}`, {
        type: 'list',
        allowBlank: true,
        showErrorMessage: true,
        errorTitle: 'Invalid value',
        error: `Please choose a value from the dropdown for ${header}.`,
        formulae: [`Lists!$${listLetter}$2:$${listLetter}$${listLength + 1}`],
      });
    };

    listColumns.forEach((column, index) => {
      addListValidation(column.key as BulkHeader, index + 1, column.values.length);
    });

    const instructions = workbook.addWorksheet('Instructions');
    instructions.columns = [{ width: 90 }];
    [
      'How to use this template',
      '1. Fill rows on the Activities sheet. Use dropdowns where available so values match the create form.',
      '2. type: required. Domains of Wellness are NOT imported — each Type maps to domains automatically:',
      '   - Game → Physical Activities, Sports, Social',
      '   - Health & Wellness → Physical, Emotional, Spiritual',
      '   - Educational → Intellectual, Vocational, Social',
      '3. Do not add a domainsOfWellness column; any such column is ignored.',
      '4. limitedCapacity: enter any whole number (not a dropdown). Leave blank if unlimited / not set.',
      '5. serviceDeliveryNeeded: Yes/No. Yes = senior bookings appear in Staff Service Delivery.',
      '6. reoccurringDays:',
      '   - Weekly: Mon–Fri only (centers closed Sat/Sun). One day, or multiple with | (example: Mon|Wed|Fri).',
      '   - Monthly: pick when in the month (example: 1st Monday, 2nd Tuesday). Monday–Friday only.',
      '7. timeMode: all_day or specific. When specific, also set startTime and endTime (HH:mm).',
      '8. fromDate / thruDate: use YYYY-MM-DD (example: 2026-09-18).',
      '9. status: active, draft, or inactive.',
      '10. Do not rename or delete the Lists sheet (it powers the dropdowns).',
    ].forEach((line, index) => {
      instructions.getCell(index + 1, 1).value = line;
      if (index === 0) {
        instructions.getCell(index + 1, 1).font = { bold: true, size: 14 };
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'activity-bulk-upload-template.xlsx';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private excelColumnLetter(columnNumber: number): string {
    let n = columnNumber;
    let letter = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      letter = String.fromCharCode(65 + rem) + letter;
      n = Math.floor((n - 1) / 26);
    }
    return letter;
  }

  private loadCenterOptions(): void {
    this.locationService.getActiveLocations().subscribe({
      next: (response) => {
        const names = (response.locations ?? [])
          .map((item) => item.locationName?.trim() ?? '')
          .filter((name) => !!name);
        this.centerOptions = names.filter((n) => n.toLowerCase() !== 'all');
      },
      error: () => {
        this.centerOptions = [];
      },
    });
  }

  triggerBulkFilePicker(): void {
    this.bulkFileInput?.nativeElement.click();
  }

  onBulkFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.bulkErrorMessage = null;
    this.bulkSuccessMessage = null;
    this.bulkRowErrors = [];

    if (!file) {
      this.bulkSelectedFile = null;
      this.bulkSelectedFileName = '';
      return;
    }

    if (!this.isSupportedBulkFile(file.name)) {
      this.bulkSelectedFile = null;
      this.bulkSelectedFileName = '';
      this.bulkErrorMessage = 'Please upload a CSV or Excel file (.csv, .xlsx, .xls).';
      input.value = '';
      return;
    }

    this.bulkSelectedFile = file;
    this.bulkSelectedFileName = file.name;
  }

  submitBulkUpload(): void {
    if (!this.bulkSelectedFile || this.isBulkUploading) {
      return;
    }

    this.isBulkUploading = true;
    this.bulkErrorMessage = null;
    this.bulkSuccessMessage = null;
    this.bulkRowErrors = [];

    const file = this.bulkSelectedFile;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const activities = this.parseBulkFile(file, reader.result);
        if (!activities.length) {
          this.isBulkUploading = false;
          this.bulkErrorMessage = 'No activity rows found in the file.';
          return;
        }
        if (activities.length > 200) {
          this.isBulkUploading = false;
          this.bulkErrorMessage = 'Bulk upload supports a maximum of 200 activities per file.';
          return;
        }

        this.activityAdminService.bulkCreateActivities(activities).subscribe({
          next: (response) => {
            this.isBulkUploading = false;
            this.bulkRowErrors = response.errors ?? [];
            this.bulkSuccessMessage =
              response.message ||
              `Created ${response.created ?? 0} of ${response.total ?? activities.length} activities.`;

            if ((response.created ?? 0) > 0) {
              this.loadActivities({ silent: true });
            }

            if ((response.failed ?? 0) === 0) {
              setTimeout(() => this.closeBulkUploadModal(), 700);
            }
          },
          error: (err) => {
            this.isBulkUploading = false;
            this.bulkErrorMessage =
              err?.error?.message ||
              err?.error?.Message ||
              'Unable to upload activities. Please try again.';
          },
        });
      } catch (error) {
        this.isBulkUploading = false;
        this.bulkErrorMessage =
          error instanceof Error ? error.message : 'Unable to read the uploaded file.';
      }
    };
    reader.onerror = () => {
      this.isBulkUploading = false;
      this.bulkErrorMessage = 'Unable to read the selected file.';
    };

    if (this.isExcelFile(file.name)) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  }

  onAddActivity(): void {
    void this.router.navigate(['/manage-services/senior-center/activity/create']);
  }

  private resetBulkUploadState(): void {
    this.isBulkUploading = false;
    this.bulkSelectedFile = null;
    this.bulkSelectedFileName = '';
    this.bulkErrorMessage = null;
    this.bulkSuccessMessage = null;
    this.bulkRowErrors = [];
    if (this.bulkFileInput?.nativeElement) {
      this.bulkFileInput.nativeElement.value = '';
    }
  }

  private isSupportedBulkFile(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return lower.endsWith('.csv') || lower.endsWith('.xlsx') || lower.endsWith('.xls');
  }

  private isExcelFile(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return lower.endsWith('.xlsx') || lower.endsWith('.xls');
  }

  private parseBulkFile(file: File, result: string | ArrayBuffer | null): ActivityCreateRequest[] {
    if (this.isExcelFile(file.name)) {
      return this.parseBulkRows(this.parseExcelRows(result));
    }
    return this.parseBulkRows(this.parseCsvRows(String(result ?? '')));
  }

  private parseExcelRows(result: string | ArrayBuffer | null): string[][] {
    if (!result) {
      return [];
    }

    const data =
      result instanceof ArrayBuffer
        ? new Uint8Array(result)
        : new TextEncoder().encode(String(result));
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    const firstSheetName =
      workbook.SheetNames.find((name) => name.toLowerCase() === 'activities') ||
      workbook.SheetNames[0];
    if (!firstSheetName) {
      return [];
    }

    const sheet = workbook.Sheets[firstSheetName];
    const matrix = XLSX.utils.sheet_to_json<(string | number | boolean | Date | null)[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });

    return matrix.map((row) =>
      (row ?? []).map((cell) => this.formatExcelCell(cell)),
    );
  }

  private formatExcelCell(cell: string | number | boolean | Date | null | undefined): string {
    if (cell == null) {
      return '';
    }
    if (cell instanceof Date && !Number.isNaN(cell.getTime())) {
      const yyyy = cell.getFullYear();
      const mm = String(cell.getMonth() + 1).padStart(2, '0');
      const dd = String(cell.getDate()).padStart(2, '0');
      const hours = cell.getHours();
      const minutes = cell.getMinutes();
      if (hours === 0 && minutes === 0 && cell.getSeconds() === 0) {
        return `${yyyy}-${mm}-${dd}`;
      }
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    return String(cell).trim();
  }

  private parseBulkRows(rows: string[][]): ActivityCreateRequest[] {
    if (rows.length < 2) {
      return [];
    }

    const headers = rows[0].map((header) => this.normalizeHeader(header));
    const requiredHeaders = ['activityname'];
    for (const required of requiredHeaders) {
      if (!headers.includes(required)) {
        throw new Error('Invalid template. Missing required column: activityName');
      }
    }

    const activities: ActivityCreateRequest[] = [];
    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i];
      if (cells.every((cell) => !String(cell ?? '').trim())) {
        continue;
      }

      const get = (key: string): string => {
        const index = headers.indexOf(key);
        return index >= 0 ? String(cells[index] ?? '').trim() : '';
      };

      const activityName = get('activityname');
      if (!activityName) {
        throw new Error(`Row ${i + 1}: Activity name is required.`);
      }

      const limitedCapacityRaw = get('limitedcapacity');
      let limitedCapacity: number | null | undefined;
      if (limitedCapacityRaw) {
        if (!/^\d+(\.0+)?$/.test(limitedCapacityRaw.trim())) {
          throw new Error(`Row ${i + 1}: limitedCapacity must be a whole number (no decimals).`);
        }
        const parsed = Number(limitedCapacityRaw);
        if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
          throw new Error(`Row ${i + 1}: limitedCapacity must be a whole number (no decimals).`);
        }
        limitedCapacity = parsed;
      }

      const daysRaw = get('reoccurringdays');
      const timeModeRaw = get('timemode').toLowerCase();
      const timeMode =
        timeModeRaw === 'all_day' || timeModeRaw === 'all day' || timeModeRaw === 'allday'
          ? 'all_day'
          : timeModeRaw === 'specific' || timeModeRaw === 'specific time'
            ? 'specific'
            : timeModeRaw || undefined;

      const status = this.normalizeBulkStatus(get('status'));
      const type = this.normalizeBulkType(get('type'));
      if (!type) {
        throw new Error(
          `Row ${i + 1}: type is required. Domains of Wellness are auto-mapped from Type.`,
        );
      }
      // Domains are never imported (multi-select). Always derive from Type.
      const domainsOfWellness = domainsForActivityType(type);
      if (domainsOfWellness.length === 0) {
        throw new Error(
          `Row ${i + 1}: type "${type}" is not recognized. Use Game, Health & Wellness, or Educational.`,
        );
      }
      const reoccurring = this.normalizeBulkReoccurring(get('reoccurring'));
      let reoccurringDays: string[] | undefined;
      if (reoccurring === 'Weekly') {
        reoccurringDays = daysRaw
          ? daysRaw
              .split(/[|;]/)
              .map((v) => normalizeWeeklyDay(v.trim()))
              .filter((v): v is string => !!v)
          : undefined;
        if (!reoccurringDays?.length) {
          throw new Error(
            `Row ${i + 1}: Weekly activities need reoccurringDays (Mon–Fri only; centers closed weekends).`,
          );
        }
      } else if (reoccurring === 'Monthly') {
        const monthly = daysRaw
          ? daysRaw
              .split(/[|;]/)
              .map((v) => normalizeMonthlyOccurrence(v.trim()))
              .find((v): v is string => !!v)
          : undefined;
        if (!monthly) {
          throw new Error(
            `Row ${i + 1}: Monthly activities need reoccurringDays as a monthly occurrence (e.g. 1st Monday, 2nd Tuesday).`,
          );
        }
        reoccurringDays = [monthly];
      }

      activities.push({
        activityName,
        code: get('code') || undefined,
        type,
        domainsOfWellness,
        description: get('description') || undefined,
        limitedCapacity,
        center: this.normalizeBulkCenter(get('center') || get('campus')),
        isSpecial: this.parseYesNo(get('isspecial')),
        isSeries: this.parseYesNo(get('isseries')),
        serviceDeliveryNeeded: this.parseYesNo(get('servicedeliveryneeded')) === true,
        timeMode,
        startTime: timeMode === 'specific' ? get('starttime') || undefined : undefined,
        endTime: timeMode === 'specific' ? get('endtime') || undefined : undefined,
        reoccurring,
        reoccurringDays,
        fromDate: get('fromdate') || undefined,
        thruDate: get('thrudate') || undefined,
        status,
      });
    }

    return activities;
  }

  private normalizeBulkType(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const match = this.typeOptions.find((option) => option.toLowerCase() === trimmed.toLowerCase());
    return match ?? trimmed;
  }

  private normalizeBulkReoccurring(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const compact = trimmed.toLowerCase().replace(/[\s_-]+/g, '');
    if (compact === 'onetime') {
      return 'One Time';
    }
    if (compact === 'weekly') {
      return 'Weekly';
    }
    if (compact === 'monthly') {
      return 'Monthly';
    }
    const match = this.reoccurringOptions.find(
      (option) => option.toLowerCase() === trimmed.toLowerCase(),
    );
    return match ?? trimmed;
  }

  private normalizeBulkCenter(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'all') {
      return undefined;
    }
    const match = this.centerOptions.find(
      (option) => option.toLowerCase() === trimmed.toLowerCase(),
    );
    return match ?? trimmed;
  }

  private toTitleCaseWords(value: string): string {
    return value
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private normalizeBulkStatus(value: string): ActivityStatus {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'draft' || normalized === 'drafted') {
      return 'draft';
    }
    if (normalized === 'inactive' || normalized === 'in-active' || normalized === 'in_active') {
      return 'inactive';
    }
    return 'active';
  }

  private parseYesNo(value: string): boolean | undefined {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (['yes', 'y', 'true', '1'].includes(normalized)) {
      return true;
    }
    if (['no', 'n', 'false', '0'].includes(normalized)) {
      return false;
    }
    return undefined;
  }

  private normalizeHeader(header: string): string {
    return header.trim().toLowerCase().replace(/[\s_-]+/g, '');
  }

  private parseCsvRows(text: string): string[][] {
    const rows: string[][] = [];
    let current: string[] = [];
    let cell = '';
    let inQuotes = false;

    const pushCell = (): void => {
      current.push(cell);
      cell = '';
    };
    const pushRow = (): void => {
      pushCell();
      rows.push(current);
      current = [];
    };

    const input = text.replace(/^\uFEFF/, '');
    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      const next = input[i + 1];

      if (inQuotes) {
        if (char === '"' && next === '"') {
          cell += '"';
          i += 1;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          cell += char;
        }
        continue;
      }

      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        pushCell();
      } else if (char === '\n') {
        pushRow();
      } else if (char === '\r') {
        if (next === '\n') {
          i += 1;
        }
        pushRow();
      } else {
        cell += char;
      }
    }

    if (cell.length > 0 || current.length > 0) {
      pushRow();
    }

    return rows;
  }

  toggleActionMenu(rowId: string, event: Event): void {
    event.stopPropagation();
    this.openActionMenuId = this.openActionMenuId === rowId ? null : rowId;
  }

  onEdit(row: ActivityListRow): void {
    this.openActionMenuId = null;
    if (!row.id) {
      return;
    }
    void this.router.navigate(['/manage-services/senior-center/activity/edit', row.id]);
  }

  /** Draft has no Active/Inactive action. */
  canToggleStatus(row: ActivityListRow): boolean {
    return row.status === 'active' || row.status === 'inactive';
  }

  /** Show the action that will be applied (opposite of current status). */
  statusToggleLabel(row: ActivityListRow): string {
    return row.status === 'active' ? 'Inactive' : 'Active';
  }

  onDuplicate(row: ActivityListRow): void {
    this.openActionMenuId = null;
    if (!row.id || this.isDuplicating) {
      return;
    }

    this.isDuplicating = true;
    this.duplicatingId = row.id;
    this.errorMessage = null;

    this.activityAdminService.getActivity(row.id).subscribe({
      next: (response) => {
        const source = response.activity;
        if (!source) {
          this.isDuplicating = false;
          this.duplicatingId = null;
          this.errorMessage = 'Unable to duplicate activity.';
          return;
        }

        const payload = this.buildDuplicatePayload(source);
        this.activityAdminService.createActivity(payload).subscribe({
          next: () => {
            this.isDuplicating = false;
            this.duplicatingId = null;
            this.loadActivities({ silent: true });
          },
          error: (err) => {
            this.isDuplicating = false;
            this.duplicatingId = null;
            this.errorMessage =
              err?.error?.message ||
              err?.error?.Message ||
              'Unable to duplicate activity. Please try again.';
          },
        });
      },
      error: (err) => {
        this.isDuplicating = false;
        this.duplicatingId = null;
        this.errorMessage =
          err?.error?.message ||
          err?.error?.Message ||
          'Unable to duplicate activity. Please try again.';
      },
    });
  }

  onToggleStatus(row: ActivityListRow): void {
    this.openActionMenuId = null;
    if (!row.id || !this.canToggleStatus(row)) {
      return;
    }

    const nextStatus: ActivityStatus = row.status === 'active' ? 'inactive' : 'active';

    this.activityAdminService.updateActivityStatus(row.id, nextStatus).subscribe({
      next: () => {
        this.loadActivities({ silent: true });
      },
      error: (err) => {
        this.errorMessage =
          err?.error?.message ||
          err?.error?.Message ||
          `Unable to mark activity ${nextStatus}.`;
      },
    });
  }

  private buildDuplicatePayload(source: AdminActivityItem): ActivityCreateRequest {
    const activityName = (source.activityName ?? '').trim() || 'Activity';
    const code = (source.code ?? '').trim() || undefined;

    return {
      activityName,
      code,
      type: source.type?.trim() || source.groupName?.trim() || undefined,
      description: source.description?.trim() || undefined,
      domainsOfWellness: Array.isArray(source.domainsOfWellness)
        ? source.domainsOfWellness.filter(Boolean)
        : undefined,
      limitedCapacity:
        source.limitedCapacity === null || source.limitedCapacity === undefined
          ? null
          : source.limitedCapacity,
      center: source.center?.trim() || undefined,
      activityImage: source.activityImage?.trim() || undefined,
      isSpecial: !!source.isSpecial,
      isSeries: !!source.isSeries,
      serviceDeliveryNeeded: !!source.serviceDeliveryNeeded,
      reoccurring: source.reoccurring?.trim() || undefined,
      timeMode: source.timeMode?.trim() || undefined,
      startTime: source.startTime?.trim() || undefined,
      endTime: source.endTime?.trim() || undefined,
      reoccurringDays: Array.isArray(source.reoccurringDays)
        ? source.reoccurringDays.filter(Boolean)
        : undefined,
      fromDate: source.fromDate?.trim() || undefined,
      thruDate: source.thruDate?.trim() || undefined,
      needsSpecialMenu: !!source.needsSpecialMenu,
      offerHotMeal: !!source.offerHotMeal,
      offerColdMeal: !!source.offerColdMeal,
      specialMenuDays: Array.isArray(source.specialMenuDays)
        ? source.specialMenuDays
        : undefined,
      // New duplicate starts as draft so staff can review before activating
      status: 'draft',
    };
  }

  private loadActivities(options: { silent?: boolean } = {}): void {
    if (!options.silent) {
      this.isInitialLoading = true;
    }
    this.errorMessage = null;

    this.activityAdminService.listActivities({ index: 0, limit: 100 }).subscribe({
      next: (response) => {
        this.activityList = (response.activities ?? []).map((item) => this.mapToRow(item));
        this.refreshActivityOptions();
        this.applyFilters();
        this.isInitialLoading = false;
      },
      error: (err) => {
        this.activityList = [];
        this.filteredList = [];
        this.activityOptions = [];
        this.isInitialLoading = false;
        this.errorMessage =
          err?.error?.message ||
          err?.error?.Message ||
          'Unable to load activities. Please try again.';
      },
    });
  }

  private mapToRow(item: AdminActivityItem): ActivityListRow {
    const domains = Array.isArray(item.domainsOfWellness)
      ? item.domainsOfWellness.filter(Boolean).join(', ')
      : '';

    const status = this.normalizeStatus(item.status);
    const fromDate = this.toDateOnly(item.fromDate);
    const thruDate = this.toDateOnly(item.thruDate);
    return {
      id: String(item.id ?? ''),
      activity: item.activityName?.trim() || '—',
      code: item.code?.trim() || '—',
      type: item.type?.trim() || item.groupName?.trim() || '—',
      special: !!item.isSpecial,
      reoccurring: this.formatReoccurring(item),
      cap: this.formatLimitedCapacity(item),
      description: item.description?.trim() || '—',
      domainsOfWellness: domains || '—',
      status,
      statusLabel: this.toStatusLabel(status),
      fromDate,
      thruDate,
    };
  }

  private toDateOnly(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const raw = String(value).trim();
    if (!raw) {
      return '';
    }
    return raw.slice(0, 10);
  }

  /** Cap column = Limited Capacity from create/edit form. */
  private formatLimitedCapacity(item: AdminActivityItem | Record<string, unknown>): string {
    const raw =
      (item as AdminActivityItem).limitedCapacity ??
      (item as Record<string, unknown>)['LimitedCapacity'] ??
      (item as Record<string, unknown>)['limited_capacity'];

    if (raw === null || raw === undefined || raw === '') {
      return 'N/A';
    }

    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) {
      return 'N/A';
    }

    return String(Math.floor(num));
  }

  private normalizeStatus(status: string | null | undefined): string {
    const value = (status ?? '').trim().toLowerCase();
    if (value === 'draft' || value === 'drafted') {
      return 'draft';
    }
    if (value === 'inactive' || value === 'in-active' || value === 'in_active') {
      return 'inactive';
    }
    if (value === 'published' || value === 'active') {
      return 'active';
    }
    return value || 'active';
  }

  private toStatusLabel(status: string): string {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'inactive':
        return 'In-Active';
      case 'active':
        return 'Active';
      default:
        return status || '—';
    }
  }

  private formatReoccurring(item: AdminActivityItem): string {
    const base = item.reoccurring?.trim() || '';
    if (!base) {
      return '—';
    }

    const days = Array.isArray(item.reoccurringDays)
      ? item.reoccurringDays.map((d) => String(d).trim()).filter(Boolean)
      : [];

    const dayShort = (day: string): string => {
      // Two letters for ambiguous days (Tu/Th, Sa/Su); match Figma-style Weekly: M Tu W Th Fr
      const map: Record<string, string> = {
        sun: 'Su',
        sunday: 'Su',
        mon: 'M',
        monday: 'M',
        tue: 'Tu',
        tuesday: 'Tu',
        wed: 'W',
        wednesday: 'W',
        thu: 'Th',
        thursday: 'Th',
        fri: 'Fr',
        friday: 'Fr',
        sat: 'Sa',
        saturday: 'Sa',
      };
      return map[day.toLowerCase()] || day;
    };

    if (base.toLowerCase() === 'weekly' && days.length) {
      return `Weekly: ${days.map(dayShort).join(' ')}`;
    }

    if (base.toLowerCase() === 'one time') {
      if (item.fromDate) {
        const raw = String(item.fromDate).slice(0, 10);
        const [y, m, d] = raw.split('-');
        if (y && m && d) {
          return `One Time: ${m}/${d}/${y}`;
        }
      }
      return 'One Time';
    }

    if (base.toLowerCase() === 'weekly') {
      return 'Weekly';
    }

    if (base.toLowerCase() === 'monthly') {
      const monthly = days.find((d) => isMonthlyOccurrenceValue(d));
      return monthly ? `Monthly: ${monthly}` : 'Monthly';
    }

    return base;
  }
}
